// BrowseSearchBar - Search and filter controls for community app browse
// Row 1: Search input (learn-style pill) + sort dropdown + admin dropdowns
// Row 2: Tag row - input on left (fixed), tags on right
// Tags: lowercase, spaces → dashes

import React, { useState, useRef, useEffect } from 'react';
import { DropdownSelect } from '../../../../../components';
import './BrowseSearchBar.css';

const SORT_OPTIONS = [
  { value: 'trending', label: 'Trending' },
  { value: 'newest', label: 'Newest' },
  { value: 'likes', label: 'Most liked' },
  { value: 'views', label: 'Most viewed' },
];

const VISIBILITY_OPTIONS = [
  { value: 'all', label: 'All apps' },
  { value: 'public', label: 'Public apps' },
  { value: 'needing_review', label: 'Needing review' },
  { value: 'not_needing_review', label: 'Not needing review' },
  { value: 'deferred', label: 'Deferred' },
  { value: 'private', label: 'Private apps' },
];

const SYNC_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'synced', label: 'Synced' },
  { value: 'neverSynced', label: 'Never synced' },
];

const TEAM_STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'teamBased', label: 'Team-based' },
  { value: 'solo', label: 'Solo' },
];

const SEARCH_BY_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'author', label: 'Author' },
];

function normalizeTag(str) {
  return str.trim().toLowerCase().replace(/\s+/g, '-');
}

export function BrowseSearchBar({
  searchQuery = '',
  searchBy = 'name',
  onSearchByChange,
  onSearchChange,
  sortBy = 'newest',
  onSortChange,
  tags = [],
  onTagsChange,
  visibility = 'public',
  onVisibilityChange,
  syncStatus = 'all',
  onSyncStatusChange,
  teamStatus = 'all',
  onTeamStatusChange,
  isAdmin = false,
}) {
  const [tagInput, setTagInput] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const handleAddTag = () => {
    const normalized = normalizeTag(tagInput);
    if (normalized && !tags.includes(normalized)) {
      onTagsChange?.([...tags, normalized]);
      setTagInput('');
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    onTagsChange?.(tags.filter((t) => t !== tagToRemove));
  };

  return (
    <div className="browse-search-bar">
      {/* Row 1: Search (learn-style) + sort dropdown */}
      <div className="browse-search-bar__row browse-search-bar__row--search">
        <div className="browse-search-bar__search">
          <i className="fa-solid fa-magnifying-glass browse-search-bar__search-icon" aria-hidden />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={searchBy === 'author' ? 'Search author username...' : 'Search community apps...'}
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.target.value)}
            className="browse-search-bar__input"
          />
        </div>
        <div className="browse-search-bar__sort">
          <label htmlFor="browse-sort-select" className="browse-search-bar__sort-label">
            Sort by
          </label>
          <DropdownSelect
            id="browse-sort-select"
            value={sortBy}
            options={SORT_OPTIONS}
            onChange={(e) => onSortChange?.(e.target.value)}
            className="browse-search-bar__sort-select"
            menuClassName="app-management-sort-menu"
          />
        </div>
        <div className="browse-search-bar__sort">
          <label htmlFor="browse-search-by-select" className="browse-search-bar__sort-label">
            Search by
          </label>
          <DropdownSelect
            id="browse-search-by-select"
            value={searchBy}
            options={SEARCH_BY_OPTIONS}
            onChange={(e) => onSearchByChange?.(e.target.value)}
            className="browse-search-bar__sort-select"
            menuClassName="app-management-sort-menu"
          />
        </div>
        {isAdmin && (
          <div className="browse-search-bar__admin-filters">
            <div className="browse-search-bar__admin-filter">
              <label htmlFor="browse-visibility-select" className="browse-search-bar__filter-label">
                Visibility
              </label>
              <DropdownSelect
                id="browse-visibility-select"
                value={visibility}
                options={VISIBILITY_OPTIONS}
                onChange={(e) => onVisibilityChange?.(e.target.value)}
                className="browse-search-bar__filter-select"
                menuClassName="app-management-sort-menu"
              />
            </div>
            <div className="browse-search-bar__admin-filter">
              <label htmlFor="browse-sync-status-select" className="browse-search-bar__filter-label">
                Sync
              </label>
              <DropdownSelect
                id="browse-sync-status-select"
                value={syncStatus}
                options={SYNC_STATUS_OPTIONS}
                onChange={(e) => onSyncStatusChange?.(e.target.value)}
                className="browse-search-bar__filter-select"
                menuClassName="app-management-sort-menu"
              />
            </div>
            <div className="browse-search-bar__admin-filter">
              <label htmlFor="browse-team-status-select" className="browse-search-bar__filter-label">
                Team
              </label>
              <DropdownSelect
                id="browse-team-status-select"
                value={teamStatus}
                options={TEAM_STATUS_OPTIONS}
                onChange={(e) => onTeamStatusChange?.(e.target.value)}
                className="browse-search-bar__filter-select"
                menuClassName="app-management-sort-menu"
              />
            </div>
          </div>
        )}
      </div>

      {/* Row 2: Tag input + tags (same row, no shared panel) */}
      <div className="browse-search-bar__row browse-search-bar__row--tags">
        <input
          type="text"
          placeholder="Add tags"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagKeyDown}
          className="browse-search-bar__tag-input"
        />
        <div className="browse-search-bar__tags">
          {tags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="browse-search-bar__tag"
              onClick={() => handleRemoveTag(tag)}
              aria-label={`Remove ${tag} tag`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
